import { Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useRecentActivity } from '../hooks/useDashboard'
import {
  Handshake,
  Briefcase,
  Users,
  Building2,
  Inbox,
  ChevronRight,
  Clock,
  CheckSquare,
} from 'lucide-react'
import { formatCurrency, formatRelativeTime } from '@/lib/utils'

/**
 * Get badge variant for deal stage
 */
function getDealStageBadge(stage: string) {
  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    lead: 'outline',
    qualified: 'secondary',
    proposal: 'secondary',
    negotiation: 'default',
    closed_won: 'default',
    closed_lost: 'destructive',
  }
  const labels: Record<string, string> = {
    lead: 'Lead',
    qualified: 'Qualified',
    proposal: 'Proposal',
    negotiation: 'Negotiation',
    closed_won: 'Won',
    closed_lost: 'Lost',
  }
  return { variant: variants[stage] || 'outline', label: labels[stage] || stage }
}

/**
 * Get badge variant for case status/priority
 */
function getCaseBadge(status: string, priority: string) {
  const statusLabels: Record<string, string> = {
    new: 'New',
    open: 'Open',
    in_progress: 'In Progress',
    resolved: 'Resolved',
    closed: 'Closed',
  }
  const priorityVariants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    low: 'outline',
    medium: 'secondary',
    high: 'default',
    urgent: 'destructive',
  }
  return {
    statusLabel: statusLabels[status] || status,
    priorityVariant: priorityVariants[priority] || 'outline',
    priorityLabel: priority.charAt(0).toUpperCase() + priority.slice(1),
  }
}

/**
 * Get badge variant for enquiry status
 */
function getEnquiryStatusBadge(status: string) {
  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    new: 'default',
    viewed: 'secondary',
    contacted: 'secondary',
    qualified: 'default',
    converted: 'outline',
    spam: 'destructive',
    archived: 'outline',
  }
  const labels: Record<string, string> = {
    new: 'New',
    viewed: 'Viewed',
    contacted: 'Contacted',
    qualified: 'Qualified',
    converted: 'Converted',
    spam: 'Spam',
    archived: 'Archived',
  }
  return { variant: variants[status] || 'outline', label: labels[status] || status }
}

/**
 * Get badge variant for task priority
 */
function getTaskBadge(status: string, priority: string) {
  const statusLabels: Record<string, string> = {
    open: 'Open',
    in_progress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
  }
  const priorityVariants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    low: 'outline',
    medium: 'secondary',
    high: 'default',
    urgent: 'destructive',
  }
  return {
    statusLabel: statusLabels[status] || status,
    priorityVariant: priorityVariants[priority] || 'outline',
    priorityLabel: priority.charAt(0).toUpperCase() + priority.slice(1),
  }
}

/**
 * RecentActivitySection Component
 *
 * Displays recent items from deals, cases, contacts, companies, and enquiries
 * Each section shows the 5 most recent items with links to view more
 */
export function RecentActivitySection() {
  const { data: activity, isLoading, error } = useRecentActivity()

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-64" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
        <p className="text-sm text-destructive">
          Failed to load recent activity. Please try again later.
        </p>
      </div>
    )
  }

  if (!activity) {
    return null
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Recent Activity</h2>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Recent Enquiries - First because these are "inbound" */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Inbox className="h-4 w-4 text-primary" />
                <CardTitle className="text-base">New Enquiries</CardTitle>
              </div>
              <Link
                to="/dashboard/enquiries"
                className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
              >
                View all
                <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            <CardDescription>Incoming leads and requests</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {activity.enquiries.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No new enquiries
              </p>
            ) : (
              activity.enquiries.map((enquiry) => {
                const badge = getEnquiryStatusBadge(enquiry.status)
                return (
                  <Link
                    key={enquiry.id}
                    to={`/dashboard/enquiries/${enquiry.id}`}
                    className="block rounded-lg border p-3 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{enquiry.name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {enquiry.company || enquiry.email}
                        </p>
                      </div>
                      <Badge variant={badge.variant} className="shrink-0 text-xs">
                        {badge.label}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {formatRelativeTime(enquiry.createdAt)}
                    </div>
                  </Link>
                )
              })
            )}
          </CardContent>
        </Card>

        {/* Recent Deals */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Handshake className="h-4 w-4 text-primary" />
                <CardTitle className="text-base">Recent Deals</CardTitle>
              </div>
              <Link
                to="/dashboard/deals"
                className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
              >
                View all
                <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            <CardDescription>Latest pipeline updates</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {activity.deals.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No deals yet
              </p>
            ) : (
              activity.deals.map((deal) => {
                const badge = getDealStageBadge(deal.stage)
                return (
                  <Link
                    key={deal.id}
                    to={`/dashboard/deals/${deal.id}`}
                    className="block rounded-lg border p-3 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{deal.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency(deal.value)}
                        </p>
                      </div>
                      <Badge variant={badge.variant} className="shrink-0 text-xs">
                        {badge.label}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {formatRelativeTime(deal.updatedAt)}
                    </div>
                  </Link>
                )
              })
            )}
          </CardContent>
        </Card>

        {/* Recent Cases */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-primary" />
                <CardTitle className="text-base">Recent Cases</CardTitle>
              </div>
              <Link
                to="/dashboard/cases"
                className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
              >
                View all
                <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            <CardDescription>Support and service tickets</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {activity.cases.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No cases yet
              </p>
            ) : (
              activity.cases.map((caseItem) => {
                const badge = getCaseBadge(caseItem.status, caseItem.priority)
                return (
                  <Link
                    key={caseItem.id}
                    to={`/dashboard/cases/${caseItem.id}`}
                    className="block rounded-lg border p-3 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{caseItem.subject}</p>
                        <p className="text-xs text-muted-foreground">
                          {badge.statusLabel}
                        </p>
                      </div>
                      <Badge variant={badge.priorityVariant} className="shrink-0 text-xs">
                        {badge.priorityLabel}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {formatRelativeTime(caseItem.updatedAt)}
                    </div>
                  </Link>
                )
              })
            )}
          </CardContent>
        </Card>

        {/* Recent Tasks */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckSquare className="h-4 w-4 text-primary" />
                <CardTitle className="text-base">Recent Tasks</CardTitle>
              </div>
              <Link
                to="/dashboard/tasks"
                className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
              >
                View all
                <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            <CardDescription>Your task activity</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {activity.tasks.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No tasks yet
              </p>
            ) : (
              activity.tasks.map((task) => {
                const badge = getTaskBadge(task.status, task.priority)
                return (
                  <Link
                    key={task.id}
                    to={`/dashboard/tasks`}
                    className="block rounded-lg border p-3 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{task.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {badge.statusLabel}
                        </p>
                      </div>
                      <Badge variant={badge.priorityVariant} className="shrink-0 text-xs">
                        {badge.priorityLabel}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {formatRelativeTime(task.updatedAt)}
                    </div>
                  </Link>
                )
              })
            )}
          </CardContent>
        </Card>

        {/* Recent Contacts */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                <CardTitle className="text-base">Recent Contacts</CardTitle>
              </div>
              <Link
                to="/dashboard/contacts"
                className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
              >
                View all
                <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            <CardDescription>People added to your CRM</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {activity.contacts.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No contacts yet
              </p>
            ) : (
              activity.contacts.map((contact) => (
                <Link
                  key={contact.id}
                  to={`/dashboard/contacts/${contact.id}`}
                  className="block rounded-lg border p-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">
                      {contact.firstName} {contact.lastName}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {contact.email || 'No email'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {formatRelativeTime(contact.updatedAt)}
                  </div>
                </Link>
              ))
            )}
          </CardContent>
        </Card>

        {/* Recent Companies */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-primary" />
                <CardTitle className="text-base">Recent Companies</CardTitle>
              </div>
              <Link
                to="/dashboard/companies"
                className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
              >
                View all
                <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            <CardDescription>Organizations in your CRM</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {activity.companies.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No companies yet
              </p>
            ) : (
              activity.companies.map((company) => (
                <Link
                  key={company.id}
                  to={`/dashboard/companies/${company.id}`}
                  className="block rounded-lg border p-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{company.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {company.industry || 'No industry set'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {formatRelativeTime(company.updatedAt)}
                  </div>
                </Link>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
